import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getBookings, updateBooking, deleteBooking, sendContractEmail, previewContractEmail, createBooking, getProducts, getPaymentStatus, getReservedDatesForProduct, getPaymentLinkStatus, getCoupons } from '../../services/api';
import { Booking } from '../../types';
import { CalendarIcon, MailIcon, EditIcon, TrashIcon, CheckIcon, XIcon, PlusIcon, EyeIcon, ArrowUpDownIcon, CreditCardIcon, ListIcon, CalendarDaysIcon, BarChart3Icon, MapPinIcon } from 'lucide-react';
import { format } from 'date-fns';
import DateRangePicker from '../../components/DateRangePicker';
import { useCompanySettings } from '../../hooks/useCompanySettings';
import BookingDetailModal from '../../components/BookingDetailModal';
import BookingStats from '../../components/BookingStats';
import { splitBookingsByCompletion, paginate } from '../../utils/bookingFilters';

type SortOption = 'created_at' | 'start_date';
type SortDirection = 'asc' | 'desc';

const BookingsManagement: React.FC = () => {
  // Lazy load SimpleBookingCalendar to avoid circular dependency
  const SimpleBookingCalendar = React.lazy(() => import('../../components/SimpleBookingCalendar'));
  const DateManagement = React.lazy(() => import('../../components/DateManagement'));
  
  const { settings: companySettings } = useCompanySettings();
  const location = useLocation();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [sorting, setSorting] = useState(false);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [editingBooking, setEditingBooking] = useState<Booking | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [viewingBooking, setViewingBooking] = useState<any | null>(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailBooking, setEmailBooking] = useState<any | null>(null);
  const [showEmailPreview, setShowEmailPreview] = useState(false);

  // Update viewingBooking when bookings list changes (for payment links updates)
  useEffect(() => {
    if (viewingBooking && bookings.length > 0) {
      const updatedBooking = bookings.find(b => b.booking_id === viewingBooking.booking_id);
      if (updatedBooking && JSON.stringify(updatedBooking) !== JSON.stringify(viewingBooking)) {
        setViewingBooking(updatedBooking);
      }
    }
  }, [bookings, viewingBooking]);
  const [emailHTML, setEmailHTML] = useState('');
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailData, setEmailData] = useState({
    includeReceipt: false,
    includeTerms: false,
    includePaymentLink: false,
    includeReviewRequest: false,
    customMessage: ''
  });
  const [products, setProducts] = useState<any[]>([]);
  const [coupons, setCoupons] = useState<any[]>([]);
  const [paymentStatuses, setPaymentStatuses] = useState<Record<string, any>>({});
  const [sortBy, setSortBy] = useState<SortOption>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    product_id: '',
    start_date: '',
    end_date: '',
    notes: '',
    address_line1: '',
    address_line2: '',
    address_city: '',
    address_state: '',
    address_zip: '',
    coupon_id: '',
    discount_amount: '',
    discount_type: '',
    discount_value: '',
    total_price: ''
  });
  const [dateAvailability, setDateAvailability] = useState<{ [date: string]: boolean }>({});
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [activeTab, setActiveTab] = useState<'list' | 'calendar' | 'stats'>('list');
  const [showDateManagement, setShowDateManagement] = useState(false);
  const [activePage, setActivePage] = useState(1);
  const [completedPage, setCompletedPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    fetchBookings();
    fetchProducts();
    fetchCoupons();
  }, []);

  // Handle navigation state from dashboard
  useEffect(() => {
    const state = location.state as any;
    if (state?.selectedBookingId && bookings.length > 0) {
      const selectedBooking = bookings.find(b => b.booking_id === state.selectedBookingId);
      if (selectedBooking) {
        setViewingBooking(selectedBooking);
        // Clear the navigation state to prevent re-opening on refresh
        navigate(location.pathname, { replace: true });
      }
    }
  }, [location.state, bookings, location.pathname, navigate]);

  useEffect(() => {
    if (editingBooking) {
      setFormData({
        customer_name: editingBooking.customer_name || '',
        customer_email: editingBooking.customer_email || '',
        customer_phone: editingBooking.customer_phone || '',
        product_id: editingBooking.product_id || '',
        start_date: editingBooking.start_date || '',
        end_date: editingBooking.end_date || '',
        notes: editingBooking.notes || '',
        address_line1: editingBooking.delivery_address?.line1 || '',
        address_line2: editingBooking.delivery_address?.line2 || '',
        address_city: editingBooking.delivery_address?.city || '',
        address_state: editingBooking.delivery_address?.state || '',
        address_zip: editingBooking.delivery_address?.postal_code || '',
        coupon_id: editingBooking.coupon_id || '',
        discount_amount: editingBooking.discount_amount?.toString() || '',
        discount_type: editingBooking.discount_type || '',
        discount_value: editingBooking.discount_value?.toString() || '',
        total_price: editingBooking.total_price?.toString() || ''
      });
      // Show the form when editing a booking
      setShowForm(true);
    }
  }, [editingBooking]);

  const handleSort = (newSortBy: SortOption, newDirection: SortDirection) => {
    // Don't do anything if already sorting the same way
    if (sorting) {
      return;
    }
    
    setSorting(true);
    setSortBy(newSortBy);
    setSortDirection(newDirection);
  };

  // Trigger fetchBookings when sort options change
  useEffect(() => {
    if (sorting) {
      fetchBookings();
    }
  }, [sortBy, sortDirection]);

  // Re-ordering or resizing the pages invalidates whatever page the user was on
  useEffect(() => {
    setActivePage(1);
    setCompletedPage(1);
  }, [sortBy, sortDirection, pageSize]);

  const fetchBookings = async (loadMore: boolean = false) => {
    try {
      const response = await getBookings(100, loadMore ? nextToken || undefined : undefined);
      const newBookings = response.items || [];
      
      let allBookings: Booking[];
      if (loadMore) {
        // Prevent duplicates by filtering out existing booking IDs
        const existingBookingIds = new Set(bookings.map((b: Booking) => b.booking_id));
        const uniqueNewBookings = newBookings.filter((booking: Booking) => !existingBookingIds.has(booking.booking_id));
        allBookings = [...bookings, ...uniqueNewBookings];
      } else {
        allBookings = newBookings;
      }
      
      // Sort ALL bookings based on current sort options
      const sortedBookings = allBookings.sort((a: Booking, b: Booking) => {
        let dateA: number, dateB: number;
        
        if (sortBy === 'created_at') {
          dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
          dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
        } else if (sortBy === 'start_date') {
          dateA = a.start_time_utc ? new Date(a.start_time_utc).getTime() : 0;
          dateB = b.start_time_utc ? new Date(b.start_time_utc).getTime() : 0;
        } else {
          dateA = 0;
          dateB = 0;
        }
        
        const result = sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
        return result;
      });
      
      setBookings(sortedBookings);
      setNextToken(response.next_token || null);
      setHasMore(!!response.next_token);
      setLoading(false);
      setSorting(false);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      setLoading(false);
      setSorting(false);
    }
  };

  // Fetch payment statuses when bookings are loaded
  useEffect(() => {
    if (bookings.length > 0) {
      const fetchPaymentStatuses = async () => {
        const statuses: Record<string, any> = {};
        
        // Collect all bookings with payment intents
        const bookingsWithPaymentIntents = bookings.filter(booking => booking.payment_intent_id);
        
        if (bookingsWithPaymentIntents.length === 0) {
          setPaymentStatuses(statuses);
          return;
        }
        
        try {
          // Fetch all payment statuses in parallel
          const paymentPromises = bookingsWithPaymentIntents.map(async (booking) => {
            try {
              const status = await getPaymentStatus(booking.payment_intent_id!);
              return { bookingId: booking.booking_id, status };
            } catch (error) {
              console.error(`Error fetching payment status for ${booking.booking_id}:`, error);
              return { bookingId: booking.booking_id, status: null };
            }
          });
          
          // Wait for all parallel requests to complete
          const results = await Promise.all(paymentPromises);
          
          // Map results to statuses object
          results.forEach(({ bookingId, status }) => {
            if (status) {
              statuses[bookingId] = status;
            }
          });
          
          setPaymentStatuses(statuses);
        } catch (error) {
          console.error('Error fetching payment statuses:', error);
          setPaymentStatuses({});
        }
      };

      fetchPaymentStatuses();
    }
  }, [bookings]);

  const fetchProducts = async () => {
    try {
      const data = await getProducts();
      setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchCoupons = async () => {
    try {
      const data = await getCoupons(false); // Get all coupons, not just active ones
      setCoupons(data);
    } catch (error) {
      // Error handling is already done in the service layer
    }
  };

  const getProductName = (productId: string) => {
    const product = products.find(p => p.product_id === productId);
    return product?.name || 'Unknown Product';
  };

  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) {
      return numbers;
    } else if (numbers.length <= 6) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    } else {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
    }
  };

  const loadAvailability = async (productId: string) => {
    if (!productId) return;
    
    setLoadingAvailability(true);
    setDateAvailability({});
    
    try {
      // Get reserved dates for the next 3 months using the fast reserved dates API
      const startDateObj = new Date();
      const endDateObj = new Date();
      endDateObj.setDate(startDateObj.getDate() + 90); // 3 months ahead
      
      const startDateStr = startDateObj.toISOString().split('T')[0];
      const endDateStr = endDateObj.toISOString().split('T')[0];
      
      // Fetch reserved dates (much faster than checking availability)
      const reservedDates = await getReservedDatesForProduct(productId, startDateStr, endDateStr);
      
      // Convert reserved dates to availability map
      const dateAvailMap: { [date: string]: boolean } = {};
      
      // Initialize all dates as available (true)
      const currentDate = new Date(startDateObj);
      while (currentDate <= endDateObj) {
        const dateStr = currentDate.toISOString().split('T')[0];
        dateAvailMap[dateStr] = true; // Assume available initially
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      // Mark reserved dates as unavailable (false)
      reservedDates.forEach((reservedDate: string) => {
        dateAvailMap[reservedDate] = false; // Mark as unavailable
      });
      
      setDateAvailability(dateAvailMap);
    } catch (error) {
      console.error('Error loading availability:', error);
      setDateAvailability({});
    } finally {
      setLoadingAvailability(false);
    }
  };

  const formatLocalDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    // Display date as UTC without local conversion
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC'
    });
  };

  const getRemainingBalance = (booking: any) => {
    // All monetary values are stored in cents, convert to dollars for calculation
    const totalPrice = parseFloat(booking.total_price || 0) / 100;
    // paid_amount already includes all payments (deposit + payment links)
    const paidAmount = parseFloat(booking.paid_amount || 0) / 100;
    return totalPrice - paidAmount;
  };

  const hasNextDayPickup = (booking: any) => {
    if (!booking.addons || !Array.isArray(booking.addons)) {
      return false;
    }
    return booking.addons.some((addon: any) => 
      addon.name?.toLowerCase().trim() === 'next day pickup'
    );
  };

  const getPaymentStatusDisplay = (booking: any) => {
    // All monetary values are stored in cents, convert to dollars
    const totalPrice = parseFloat(booking.total_price || 0) / 100;
    // paid_amount already includes all payments (deposit + payment links)
    const totalPaidAmount = parseFloat(booking.paid_amount || 0) / 100;

    if (totalPaidAmount >= totalPrice) {
      return 'Paid';
    } else if (totalPaidAmount > 0) {
      return 'Partially Paid';
    }
    return 'Not Paid';
  };

  const { active: activeBookings, completed: completedBookings } =
    splitBookingsByCompletion(bookings);

  const handleStatusUpdate = async (bookingId: string, status: Booking['status']) => {
    try {
      await updateBooking(bookingId, { status });
      fetchBookings();
    } catch (error) {
      console.error('Error updating booking:', error);
    }
  };

  const handleEmailCustomer = async (booking: any) => {
    // Fetch complete booking data to get payment links and payment status
    try {
      // Find the booking in our current state with full details
      const fullBooking = bookings.find(b => b.booking_id === booking.booking_id);
      
      if (fullBooking) {
        setEmailBooking(fullBooking);
      } else {
        // Fallback to the basic booking data
        setEmailBooking(booking);
      }
    } catch (error) {
      console.error('Error fetching booking details for email:', error);
      setEmailBooking(booking);
    }
    setShowEmailModal(true);
  };

  const handleEmailPreview = async () => {
    if (!emailBooking || isPreviewLoading) return;
    setIsPreviewLoading(true);
    try {
      const response = await previewContractEmail(emailBooking.booking_id, {
        include_receipt: emailData.includeReceipt,
        include_terms: emailData.includeTerms,
        include_payment_link: emailData.includePaymentLink,
        include_review_request: emailData.includeReviewRequest,
        custom_message: emailData.customMessage
      });
      setEmailHTML(response.html);
      setShowEmailPreview(true);
    } catch (error) {
      console.error('Error generating email preview:', error);
      alert('Failed to generate email preview');
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailBooking || isSendingEmail) return;
    setIsSendingEmail(true);
    
    try {
      // Payment links are only created from the info page, not here
      
      // Send the email via the backend with options
      await sendContractEmail(emailBooking.booking_id, {
        include_receipt: emailData.includeReceipt,
        include_terms: emailData.includeTerms,
        include_payment_link: emailData.includePaymentLink,
        include_review_request: emailData.includeReviewRequest,
        custom_message: emailData.customMessage
      });
      
      alert('Email sent successfully!');
      setShowEmailModal(false);
      setShowEmailPreview(false);
      setEmailHTML('');
      setEmailData({
        includeReceipt: false,
        includeTerms: false,
        includePaymentLink: false,
        includeReviewRequest: false,
        customMessage: ''
      });
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Failed to send email');
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleDelete = async (bookingId: string) => {
    if (!window.confirm('Are you sure you want to delete this booking? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteBooking(bookingId);
      
      // Show success message
      const successMessage = document.createElement('div');
      successMessage.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center';
      successMessage.innerHTML = `
        <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
        </svg>
        Booking deleted successfully
      `;
      document.body.appendChild(successMessage);
      setTimeout(() => successMessage.remove(), 3000);
      
      // Refresh the bookings list
      await fetchBookings();
    } catch (error: any) {
      console.error('Error deleting booking:', error);
      
      // Log detailed error information
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
        console.error('Response headers:', error.response.headers);
      } else if (error.request) {
        console.error('No response received:', error.request);
      } else {
        console.error('Error message:', error.message);
      }
      
      // Show user-friendly error message
      if (error.response?.status === 404) {
        alert('Booking not found. It may have already been deleted.');
      } else if (error.response?.status === 400) {
        alert('Invalid booking data. Please try again.');
      } else {
        alert('Failed to delete booking. Please try again later.');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingBooking) {
        // Update existing booking
        const updateData: any = {
          customer: {
            full_name: formData.customer_name,
            email: formData.customer_email,
            phone: formData.customer_phone
          },
          notes: formData.notes
        };
        
        // Add discount fields if they have values
        if (formData.discount_amount) {
          updateData.discount_amount = parseFloat(formData.discount_amount) || 0;
        }
        if (formData.discount_type) {
          updateData.discount_type = formData.discount_type;
        }
        if (formData.discount_value) {
          updateData.discount_value = parseFloat(formData.discount_value) || 0;
        }
        if (formData.total_price) {
          updateData.total_price = parseFloat(formData.total_price) || 0;
        }
        
        await updateBooking(editingBooking.booking_id, updateData);
        
        // Update the local bookings list to reflect changes
        setBookings(prevBookings => 
          prevBookings.map(booking => 
            booking.booking_id === editingBooking.booking_id 
              ? { ...booking, ...updateData }
              : booking
          )
        );
        
        // Update the editing booking state to reflect changes in the form
        setEditingBooking(prev => 
          prev ? { ...prev, ...updateData } : null
        );
        
        // Update the viewing booking state if this booking is currently being viewed
        setViewingBooking((prev: any) => 
          prev && prev.booking_id === editingBooking.booking_id 
            ? { ...prev, ...updateData }
            : prev
        );
        
        // Show success message
        alert('Booking updated successfully!');
      } else {
        // Create new booking - calculate payment amounts
        const product = products.find(p => p.product_id === formData.product_id);
        if (!product) {
          alert('Product not found');
          return;
        }
        
        // Calculate rental days
        const start = new Date(formData.start_date);
        const end = new Date(formData.end_date);
        const days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
        
        // Calculate total price (price per day * days)
        const pricePerDay = product.price?.amount_cents ? product.price.amount_cents / 100 : 0;
        const subtotal = pricePerDay * days;
        
        // Add tax (8%)
        const taxRate = 0.08;
        const totalPrice = subtotal * (1 + taxRate);
        
        // Create UTC dates for booking
        // Parse dates as UTC to avoid timezone conversion issues
        // formData.start_date and end_date are in YYYY-MM-DD format
        const startUTC = new Date(formData.start_date + 'T12:00:00Z');
        const endUTC = new Date(formData.end_date + 'T14:00:00Z');
        
        // Format dates without milliseconds (YYYY-MM-DDTHH:MM:SSZ)
        const formatDateWithoutMs = (date: Date) => {
          return date.toISOString().replace(/\.\d{3}Z$/, 'Z');
        };
        
        // Calculate discount if coupon is applied
        let discountAmount = 0;
        let finalTotalPrice = totalPrice;
        const selectedCoupon = formData.coupon_id ? coupons.find(c => c.coupon_id === formData.coupon_id) : null;
        
        if (selectedCoupon) {
          if (selectedCoupon.discount_type === 'percentage') {
            discountAmount = totalPrice * (selectedCoupon.discount_value / 100);
          } else {
            discountAmount = selectedCoupon.discount_value;
          }
          finalTotalPrice = totalPrice - discountAmount;
        }
        
        const bookingData = {
          product_id: formData.product_id,
          start_date: formatDateWithoutMs(startUTC),
          end_date: formatDateWithoutMs(endUTC),
          customer: {
            full_name: formData.customer_name,
            email: formData.customer_email,
            phone: formData.customer_phone
          },
          delivery_address: {
            line1: formData.address_line1,
            line2: formData.address_line2,
            city: formData.address_city,
            state: formData.address_state,
            postal_code: formData.address_zip
          },
          notes: formData.notes,
          coupon_id: formData.coupon_id || undefined,
          coupon_code: selectedCoupon?.code || undefined,
          discount_amount: Math.round(discountAmount * 100), // Convert to cents
          discount_type: selectedCoupon?.discount_type || undefined,
          discount_value: selectedCoupon?.discount_value || undefined,
          deposit_amount: 0, // In cents
          total_price: Math.round(finalTotalPrice * 100), // Convert dollars to cents (after discount)
          paid_amount: 0 // In cents
        };
        await createBooking(bookingData);
      }
      
      fetchBookings();
      setShowForm(false);
      setEditingBooking(null);
      setFormData({
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        product_id: '',
        start_date: '',
        end_date: '',
        notes: '',
        address_line1: '',
        address_line2: '',
        address_city: '',
        address_state: '',
        address_zip: '',
        coupon_id: '',
        discount_amount: '',
        discount_type: '',
        discount_value: '',
        total_price: ''
      });
    } catch (error) {
      console.error('Error saving booking:', error);
      alert('Failed to save booking');
    }
  };

  const handleCancelBooking = () => {
    setShowForm(false);
    setEditingBooking(null);
    setFormData({
      customer_name: '',
      customer_email: '',
      customer_phone: '',
      product_id: '',
      start_date: '',
      end_date: '',
      notes: '',
      address_line1: '',
      address_line2: '',
      address_city: '',
      address_state: '',
      address_zip: '',
      coupon_id: '',
      discount_amount: '',
      discount_type: '',
      discount_value: '',
      total_price: ''
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const renderBookingCard = (booking: any, index: number) => (
    <div 
      key={`${booking.booking_id}-${index}`} 
      className="p-4 space-y-3 cursor-pointer hover:bg-gray-50"
      onClick={() => setViewingBooking(booking)}
    >
      {/* Customer Info & Status */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="font-medium text-gray-900 truncate">{booking.customer_name || booking.customer?.full_name || 'Unknown'}</div>
          <div className="text-sm text-gray-500 truncate">{booking.customer_email || booking.customer?.email || 'No email'}</div>
          <div className="text-sm text-gray-500">{booking.customer_phone || booking.customer?.phone || 'No phone'}</div>
        </div>
        <div className="flex flex-col gap-1 items-end flex-shrink-0">
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${
            booking.status === 'CONFIRMED' || booking.status === 'confirmed'
              ? 'bg-green-100 text-green-800'
              : booking.status === 'DROPPED_OFF' || booking.status === 'dropped_off'
              ? 'bg-blue-100 text-blue-800'
              : booking.status === 'COMPLETED' || booking.status === 'completed'
              ? 'bg-purple-100 text-purple-800'
              : booking.status === 'PENDING' || booking.status === 'pending'
              ? 'bg-yellow-100 text-yellow-800'
              : 'bg-red-100 text-red-800'
          }`}>
            {booking.status || 'unknown'}
          </span>
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${
            getPaymentStatusDisplay(booking) === 'Paid' ? 'bg-green-100 text-green-800' :
            getPaymentStatusDisplay(booking) === 'Partially Paid' ? 'bg-yellow-100 text-yellow-800' :
            getPaymentStatusDisplay(booking) === 'Not Paid' ? 'bg-red-100 text-red-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            {getPaymentStatusDisplay(booking)}
          </span>
        </div>
      </div>

      {/* Product & Dates */}
      <div className="space-y-2 text-sm">
        <div>
          <div className="font-medium text-gray-900">{getProductName(booking.product_id)}</div>
          <div className="text-gray-500 text-xs mt-1">
            {formatLocalDate(booking.start_time_utc || booking.start_date)} → {formatLocalDate(booking.end_time_utc || booking.end_date)}
          </div>
          {hasNextDayPickup(booking) && (
            <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 mt-1">
              Next Day Pickup
            </span>
          )}
        </div>

        {/* Payment Info */}
        <div className="flex justify-between items-center pt-2 border-t border-gray-100">
          <div>
            <div className="text-xs text-gray-500">Paid</div>
            <div className="font-medium text-gray-900">
              ${(() => {
                const totalPaid = parseFloat(String(booking.paid_amount || 0)) / 100;
                return totalPaid.toFixed(2);
              })()}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-gray-500">Total</div>
            <div className="font-medium text-gray-900">
              ${booking.total_price ? (parseFloat(String(booking.total_price)) / 100).toFixed(2) : '0.00'}
            </div>
          </div>
        </div>

        {/* Address */}
        {booking.delivery_address && (
          <div className="text-xs text-gray-500 pt-1">
            <div className="flex items-start">
              <MapPinIcon className="h-3 w-3 mr-1 mt-0.5 flex-shrink-0" />
              <div className="truncate">
                {booking.delivery_address.line1}, {booking.delivery_address.city}, {booking.delivery_address.state}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-2" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={(e) => { e.stopPropagation(); setViewingBooking(booking); }}
          className="flex-1 px-3 py-2 text-xs font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 flex items-center justify-center"
        >
          <EyeIcon className="h-3 w-3 mr-1" />
          View
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); setEmailBooking(booking); setShowEmailModal(true); }}
          className="flex-1 px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 flex items-center justify-center"
        >
          <MailIcon className="h-3 w-3 mr-1" />
          Email
        </button>
      </div>
    </div>
  );

  const renderBookingRow = (booking: any, index: number) => (
    <tr key={`${booking.booking_id}-${index}`} className="hover:bg-gray-50 cursor-pointer" onClick={() => setViewingBooking(booking)}>
      <td className="px-6 py-4">
        <div>
          <div className="text-sm font-medium text-gray-900">{booking.customer_name || booking.customer?.full_name || 'Unknown'}</div>
          <div className="text-sm text-gray-500">{booking.customer_email || booking.customer?.email || 'No email'}</div>
          <div className="text-sm text-gray-500">{booking.customer_phone || booking.customer?.phone || 'No phone'}</div>
        </div>
      </td>
      <td className="px-6 py-4">
        <div className="text-sm font-medium text-gray-900">{getProductName(booking.product_id)}</div>
      </td>
      <td className="px-6 py-4">
        {booking.delivery_address ? (
          <div className="text-sm text-gray-900">
            <div>{booking.delivery_address.line1}</div>
            {booking.delivery_address.line2 && <div>{booking.delivery_address.line2}</div>}
            <div>{booking.delivery_address.city}, {booking.delivery_address.state} {booking.delivery_address.postal_code}</div>
          </div>
        ) : (
          <span className="text-sm text-gray-400">No address</span>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          booking.status === 'CONFIRMED' || booking.status === 'confirmed'
            ? 'bg-green-100 text-green-800'
            : booking.status === 'DROPPED_OFF' || booking.status === 'dropped_off'
            ? 'bg-blue-100 text-blue-800'
            : booking.status === 'COMPLETED' || booking.status === 'completed'
            ? 'bg-purple-100 text-purple-800'
            : booking.status === 'PENDING' || booking.status === 'pending'
            ? 'bg-yellow-100 text-yellow-800'
            : 'bg-red-100 text-red-800'
        }`}>
          {booking.status || 'unknown'}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-gray-900">
          {formatLocalDate(booking.start_time_utc || booking.start_date)}
          <br />
          {formatLocalDate(booking.end_time_utc || booking.end_date)}
          {hasNextDayPickup(booking) && (
            <div className="mt-1">
              <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                Next Day Pickup
              </span>
            </div>
          )}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          getPaymentStatusDisplay(booking) === 'Paid' ? 'bg-green-100 text-green-800' :
          getPaymentStatusDisplay(booking) === 'Partially Paid' ? 'bg-yellow-100 text-yellow-800' :
          getPaymentStatusDisplay(booking) === 'Not Paid' ? 'bg-red-100 text-red-800' :
          'bg-gray-100 text-gray-800'
        }`}>
          {getPaymentStatusDisplay(booking)}
        </span>
      </td>
      <td className="px-6 py-4">
        <div className="text-sm">
          <div className="font-medium text-gray-900">
            Paid: ${(() => {
              // Convert from cents to dollars - paid_amount already includes all payments
              const totalPaid = parseFloat(String(booking.paid_amount || 0)) / 100;
              return totalPaid.toFixed(2);
            })()}
          </div>
          <div className="text-gray-600">
            Total: ${booking.total_price ? (parseFloat(String(booking.total_price)) / 100).toFixed(2) : '0.00'}
          </div>
          {booking.payment_intent_id && (
            <div className="text-xs text-gray-400 mt-1" title={booking.payment_intent_id}>
              ID: {booking.payment_intent_id.substring(0, 12)}...
            </div>
          )}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {booking.created_at ? format(new Date(booking.created_at), 'MMM dd, yyyy') : 'N/A'}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" onClick={(e) => e.stopPropagation()}>
        <div className="flex space-x-2">
          <button
            onClick={(e) => { e.stopPropagation(); setViewingBooking(booking); }}
            className="text-blue-600 hover:text-blue-900"
            title="View Details"
          >
            <EyeIcon className="h-4 w-4" />
          </button>
          {(booking.status === 'PENDING' || booking.status === 'pending') && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); handleStatusUpdate(booking.booking_id, 'confirmed'); }}
                className="text-green-600 hover:text-green-900"
                title="Confirm"
              >
                <CheckIcon className="h-4 w-4" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); handleStatusUpdate(booking.booking_id, 'cancelled'); }}
                className="text-red-600 hover:text-red-900"
                title="Cancel"
              >
                <XIcon className="h-4 w-4" />
              </button>
            </>
          )}
          {(booking.status === 'CONFIRMED' || booking.status === 'confirmed' ||
            booking.status === 'DROPPED_OFF' || booking.status === 'dropped_off' ||
            booking.status === 'COMPLETED' || booking.status === 'completed') && (
            <button
              onClick={(e) => { e.stopPropagation(); handleEmailCustomer(booking); }}
              className="text-blue-600 hover:text-blue-900"
              title="Email Customer"
            >
              <MailIcon className="h-4 w-4" />
            </button>
          )}
          {/*<button
            onClick={(e) => { e.stopPropagation(); setEditingBooking(booking); }}
            className="text-gray-600 hover:text-gray-900"
            title="Edit"
          > 
            <EditIcon className="h-4 w-4" />
          </button> */}
          <button
            onClick={(e) => { e.stopPropagation(); handleDelete(booking.booking_id); }}
            className="text-red-600 hover:text-red-900"
            title="Delete"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  );

  const renderBookingsSection = (
    title: string,
    sectionBookings: Booking[],
    page: number,
    onPageChange: (page: number) => void,
    countClasses: string,
    emptyMessage: string
  ) => {
    const {
      items: pageBookings,
      currentPage,
      totalPages,
      total,
      startIndex,
      endIndex
    } = paginate(sectionBookings, page, pageSize);

    return (
      <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
        <div className="px-4 py-3 border-b border-gray-200 sm:px-6 sm:py-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            {title}
            <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${countClasses}`}>
              {total}
            </span>
          </h3>
        </div>

        {total === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-gray-500 sm:px-6">
            {emptyMessage}
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="md:hidden">
              <div className="divide-y divide-gray-200">
                {pageBookings.map((booking, index) => renderBookingCard(booking, index))}
              </div>
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Product
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Delivery Address
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('start_date', 'desc')}
                    >
                      <div className="flex items-center">
                        Dates
                        {sortBy === 'start_date' && (
                          <ArrowUpDownIcon className={`ml-1 h-3 w-3 ${sortDirection === 'asc' ? 'text-blue-600' : 'text-blue-600 rotate-180'}`} />
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Payment Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Payment Details
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('created_at', 'desc')}
                    >
                      <div className="flex items-center">
                        Created
                        {sortBy === 'created_at' && (
                          <ArrowUpDownIcon className={`ml-1 h-3 w-3 ${sortDirection === 'asc' ? 'text-blue-600' : 'text-blue-600 rotate-180'}`} />
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pageBookings.map((booking, index) => renderBookingRow(booking, index))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-4 py-3 border-t border-gray-200 sm:px-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <p className="text-sm text-gray-500">
                Showing <span className="font-medium text-gray-900">{startIndex + 1}</span>
                {'-'}
                <span className="font-medium text-gray-900">{endIndex}</span> of{' '}
                <span className="font-medium text-gray-900">{total}</span>
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onPageChange(currentPage - 1)}
                  disabled={currentPage <= 1}
                  className="px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600 whitespace-nowrap">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => onPageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  className="px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bookings Management</h1>
          <p className="text-gray-600 mt-1">Manage your bookings, availability, and special offers</p>
        </div>
        <div className="flex flex-wrap gap-3 mt-4 md:mt-0">
          {/* <button
            onClick={() => setShowDateManagement(true)}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center"
          >
            <CalendarDaysIcon className="h-4 w-4 mr-2" />
            Date Management
          </button> */}
          <button
            onClick={() => setShowForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            New Booking
          </button>
        </div>
      </div>

      {/* Tabs */}
      {/* overflow-y-hidden is load-bearing: setting overflow-x makes the
          computed overflow-y "auto", and the nav's -mb-px leaves it 1px taller
          than this box, which is enough to raise a vertical scrollbar. */}
      <div className="border-b border-gray-200 mb-6 overflow-x-auto overflow-y-hidden">
        <nav className="-mb-px flex space-x-4 sm:space-x-8 min-w-max">
          <button
            onClick={() => setActiveTab('list')}
            className={`
              py-2 px-1 border-b-2 font-medium text-sm transition-colors
              ${activeTab === 'list'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <div className="flex items-center">
              <ListIcon className="h-4 w-4 mr-2" />
              List View
            </div>
          </button>
          <button
            onClick={() => setActiveTab('calendar')}
            className={`
              py-2 px-1 border-b-2 font-medium text-sm transition-colors
              ${activeTab === 'calendar'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <div className="flex items-center">
              <CalendarIcon className="h-4 w-4 mr-2" />
              Calendar View
            </div>
          </button>
          <button
            onClick={() => setActiveTab('stats')}
            className={`
              py-2 px-1 border-b-2 font-medium text-sm transition-colors
              ${activeTab === 'stats'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <div className="flex items-center">
              <BarChart3Icon className="h-4 w-4 mr-2" />
              Stats
            </div>
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'calendar' ? (
        <React.Suspense fallback={
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        }>
          <SimpleBookingCalendar onBookingClick={setViewingBooking} />
        </React.Suspense>
      ) : activeTab === 'stats' ? (
        <BookingStats />
      ) : (
        <>
          {showForm && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {editingBooking ? 'Edit Booking' : 'Create New Booking'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Customer Name
                </label>
                <input
                  type="text"
                  required
                  value={formData.customer_name}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Customer Email
                </label>
                <input
                  type="email"
                  required
                  value={formData.customer_email}
                  onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Customer Phone
                </label>
                <input
                  type="tel"
                  required
                  value={formData.customer_phone}
                  onChange={(e) => {
                    const formatted = formatPhoneNumber(e.target.value);
                    setFormData({ ...formData, customer_phone: formatted });
                  }}
                  placeholder="XXX-XXX-XXXX"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Product
                </label>
                <select
                  required
                  value={formData.product_id}
                  onChange={(e) => {
                    const productId = e.target.value;
                    setFormData({ ...formData, product_id: productId });
                    loadAvailability(productId);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a product</option>
                  {products.map(product => (
                    <option key={product.product_id} value={product.product_id}>
                      {product.name} - ${product.price?.amount_cents ? (product.price.amount_cents / 100).toFixed(2) : '0.00'}/day
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rental Dates
                {loadingAvailability && <span className="ml-2 text-xs text-gray-500">(Loading availability...)</span>}
              </label>
              {!formData.product_id ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 text-sm text-yellow-800">
                  Please select a product first to view available dates
                </div>
              ) : (
                <DateRangePicker
                  startDate={formData.start_date}
                  endDate={formData.end_date}
                  onStartDateChange={(date: string) => {
                    setFormData(prev => ({ ...prev, start_date: date }));
                  }}
                  onEndDateChange={(date: string) => {
                    setFormData(prev => ({ ...prev, end_date: date }));
                  }}
                  minDate={new Date().toISOString().split('T')[0]}
                  availabilityMap={dateAvailability}
                  productId={formData.product_id}
                />
              )}
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Delivery Address</h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Address Line 1
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.address_line1}
                    onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Street address"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Address Line 2 (Optional)
                  </label>
                  <input
                    type="text"
                    value={formData.address_line2}
                    onChange={(e) => setFormData({ ...formData, address_line2: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Apt, suite, etc."
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      City
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.address_city}
                      onChange={(e) => setFormData({ ...formData, address_city: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      State
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.address_state}
                      onChange={(e) => setFormData({ ...formData, address_state: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      maxLength={2}
                      placeholder="GA"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      ZIP Code
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.address_zip}
                      onChange={(e) => setFormData({ ...formData, address_zip: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      maxLength={5}
                      placeholder="30301"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Coupon Selection - Only show for new bookings */}
            {!editingBooking && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Apply Coupon Code (Optional)
                </label>
                <select
                  value={formData.coupon_id}
                  onChange={(e) => {
                    const selectedCoupon = coupons.find(c => c.coupon_id === e.target.value);
                    if (selectedCoupon) {
                      setFormData({
                        ...formData,
                        coupon_id: selectedCoupon.coupon_id,
                        discount_type: selectedCoupon.discount_type,
                        discount_value: selectedCoupon.discount_value.toString()
                      });
                    } else {
                      setFormData({
                        ...formData,
                        coupon_id: '',
                        discount_type: '',
                        discount_value: ''
                      });
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">No Coupon</option>
                  {coupons.map(coupon => (
                    <option key={coupon.coupon_id} value={coupon.coupon_id}>
                      {coupon.code} - {coupon.discount_type === 'percentage' ? `${coupon.discount_value}%` : `$${coupon.discount_value}`} off
                      {!coupon.is_active ? ' (Inactive)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            {/* Discount Information - Only show for editing existing bookings */}
            {editingBooking && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Discount Information</h4>
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Discount Amount
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.discount_amount}
                        onChange={(e) => setFormData({ ...formData, discount_amount: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Discount Type
                      </label>
                      <select
                        value={formData.discount_type}
                        onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">No Discount</option>
                        <option value="percentage">Percentage</option>
                        <option value="fixed">Fixed Amount</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Discount Value
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.discount_value}
                        onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={formData.discount_type === 'percentage' ? '5' : '10.00'}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Total Price
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.total_price}
                      onChange={(e) => setFormData({ ...formData, total_price: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes (Optional)
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Any special requirements or notes"
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCancelBooking}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-blue-700"
              >
                {editingBooking ? 'Update Booking' : 'Create Booking'}
              </button>
            </div>
          </form>
        </div>
          )}

          {/* List Controls */}
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="px-4 py-3 sm:px-6 sm:py-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <h3 className="text-lg font-medium text-gray-900">All Bookings ({bookings.length})</h3>
                
                {/* Sort Buttons */}
                <div className="flex items-center flex-wrap gap-2">
                  <span className="text-sm text-gray-500">Sort:</span>
                  
                  {/* Rental Date Sort */}
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => handleSort('start_date', 'desc')}
                      disabled={sorting}
                      className={`px-3 py-1 text-xs font-medium rounded-md transition-colors flex items-center ${
                        sortBy === 'start_date' && sortDirection === 'desc' && !sorting
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      } ${sorting ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {sorting && sortBy === 'start_date' && sortDirection === 'desc' ? (
                        <>
                          <div className="animate-spin rounded-full h-3 w-3 border border-current border-t-transparent mr-1"></div>
                          Sorting...
                        </>
                      ) : (
                        'Rental (Newest)'
                      )}
                    </button>
                    <button
                      onClick={() => handleSort('start_date', 'asc')}
                      disabled={sorting}
                      className={`px-3 py-1 text-xs font-medium rounded-md transition-colors flex items-center ${
                        sortBy === 'start_date' && sortDirection === 'asc' && !sorting
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      } ${sorting ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {sorting && sortBy === 'start_date' && sortDirection === 'asc' ? (
                        <>
                          <div className="animate-spin rounded-full h-3 w-3 border border-current border-t-transparent mr-1"></div>
                          Sorting...
                        </>
                      ) : (
                        'Rental (Oldest)'
                      )}
                    </button>
                  </div>
                  
                  {/* Created Date Sort */}
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => handleSort('created_at', 'desc')}
                      disabled={sorting}
                      className={`px-3 py-1 text-xs font-medium rounded-md transition-colors flex items-center ${
                        sortBy === 'created_at' && sortDirection === 'desc' && !sorting
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      } ${sorting ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {sorting && sortBy === 'created_at' && sortDirection === 'desc' ? (
                        <>
                          <div className="animate-spin rounded-full h-3 w-3 border border-current border-t-transparent mr-1"></div>
                          Sorting...
                        </>
                      ) : (
                        'Created (Newest)'
                      )}
                    </button>
                    <button
                      onClick={() => handleSort('created_at', 'asc')}
                      disabled={sorting}
                      className={`px-3 py-1 text-xs font-medium rounded-md transition-colors flex items-center ${
                        sortBy === 'created_at' && sortDirection === 'asc' && !sorting
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      } ${sorting ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {sorting && sortBy === 'created_at' && sortDirection === 'asc' ? (
                        <>
                          <div className="animate-spin rounded-full h-3 w-3 border border-current border-t-transparent mr-1"></div>
                          Sorting...
                        </>
                      ) : (
                        'Created (Oldest)'
                      )}
                    </button>
                  </div>

                  {/* Page Size */}
                  <div className="flex items-center space-x-1">
                    <span className="text-sm text-gray-500">Show:</span>
                    <select
                      value={pageSize}
                      onChange={(e) => setPageSize(Number(e.target.value))}
                      className="px-2 py-1 text-xs font-medium border border-gray-300 rounded-md bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {[10, 25, 50, 100].map((size) => (
                        <option key={size} value={size}>{size} per page</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Not Completed Bookings */}
          {renderBookingsSection(
            'Not Completed',
            activeBookings,
            activePage,
            setActivePage,
            'bg-yellow-100 text-yellow-800',
            'No outstanding bookings.'
          )}

          {/* Completed Bookings */}
          {renderBookingsSection(
            'Completed',
            completedBookings,
            completedPage,
            setCompletedPage,
            'bg-purple-100 text-purple-800',
            'No completed bookings yet.'
          )}

          {/* Load More Button */}
          {hasMore && (
            <div className="bg-white rounded-lg shadow px-4 py-3 sm:px-6 sm:py-4">
              <button
                onClick={() => fetchBookings(true)}
                disabled={loading}
                className="w-full flex justify-center items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                    Loading...
                  </>
                ) : (
                  'Load More Bookings'
                )}
              </button>
            </div>
          )}
        </>
      )}

      {/* Date Management Modal */}
      {/* {showDateManagement && (
        <React.Suspense fallback={
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
          </div>
        }>
          <DateManagement onClose={() => setShowDateManagement(false)} />
        </React.Suspense>
      )} */}

      {/* Booking Detail Modal */}
      {viewingBooking && (
        <BookingDetailModal
          key={`booking-${viewingBooking.booking_id}-${viewingBooking.total_price}-${viewingBooking.discount_amount}-${viewingBooking.discount_type}-${viewingBooking.discount_value}-${(viewingBooking.payment_links || []).length}`}
          booking={viewingBooking}
          onClose={() => setViewingBooking(null)}
          onRefresh={fetchBookings}
          onReopen={() => {
            // Reopen the modal with the updated booking data
            setTimeout(() => {
              setViewingBooking((prev: any) => {
                // Find the updated booking in the bookings list
                const updatedBooking = bookings.find(b => b.booking_id === viewingBooking.booking_id);
                return updatedBooking || viewingBooking;
              });
            }, 200);
          }}
        />
      )}

      {/* Email Modal */}
      {showEmailModal && emailBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">Send Email to Customer</h2>
                <button
                  onClick={() => {
                    setShowEmailModal(false);
                    setShowEmailPreview(false);
                    setEmailHTML('');
                    setEmailData({
                      includeReceipt: false,
                      includeTerms: false,
                      includePaymentLink: false,
                      includeReviewRequest: false,
                      customMessage: ''
                    });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <p className="text-sm text-gray-600">
                  <strong>Customer:</strong> {emailBooking.customer?.full_name || emailBooking.customer_name}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Email:</strong> {emailBooking.customer?.email || emailBooking.customer_email}
                </p>
              </div>

              <div className="space-y-3">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={emailData.includeReceipt}
                    onChange={(e) => setEmailData({ ...emailData, includeReceipt: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm font-medium text-gray-700">Include Receipt</span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={emailData.includeTerms}
                    onChange={(e) => setEmailData({ ...emailData, includeTerms: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm font-medium text-gray-700">Include Terms & Conditions</span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={emailData.includePaymentLink}
                    onChange={(e) => setEmailData({ ...emailData, includePaymentLink: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm font-medium text-gray-700">Include Payment Link</span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={emailData.includeReviewRequest}
                    onChange={(e) => setEmailData({ ...emailData, includeReviewRequest: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm font-medium text-gray-700">Include Google Review Request</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Custom Message (Optional)
                </label>
                <textarea
                  value={emailData.customMessage}
                  onChange={(e) => setEmailData({ ...emailData, customMessage: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Add a custom message to include in the email..."
                />
              </div>

              {showEmailPreview && (
                <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                  <h3 className="text-lg font-semibold mb-2">Email Preview</h3>
                  <div 
                    className="prose max-w-none"
                    dangerouslySetInnerHTML={{ __html: emailHTML }}
                  />
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowEmailModal(false);
                  setShowEmailPreview(false);
                  setEmailHTML('');
                  setEmailData({
                    includeReceipt: false,
                    includeTerms: false,
                    includePaymentLink: false,
                    includeReviewRequest: false,
                    customMessage: ''
                  });
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleEmailPreview}
                disabled={isPreviewLoading || isSendingEmail}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isPreviewLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Loading...
                  </>
                ) : (
                  'Preview'
                )}
              </button>
              <button
                onClick={handleSendEmail}
                disabled={isPreviewLoading || isSendingEmail}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isSendingEmail ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Sending...
                  </>
                ) : (
                  'Send Email'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingsManagement;
